import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * A colour field as Product Master has it: C / M / Y / K to switch on, the
 * Pantone colours as tags, and a search of the Pantone master to add more.
 * The value stays the plain text the job card prints: "K + 485c RED".
 */
const CMYK = ['C', 'M', 'Y', 'K'] as const;

function parseParts(val: string): { cmyk: Set<string>; spots: string[] } {
  const parts = val.split('+').map(s => s.trim()).filter(Boolean);
  const cmyk = new Set<string>();
  const spots: string[] = [];
  for (const p of parts) {
    if ((CMYK as readonly string[]).includes(p)) cmyk.add(p);
    else spots.push(p);
  }
  return { cmyk, spots };
}

function buildValue(cmyk: Set<string>, spots: string[]): string {
  const all = [...CMYK.filter(c => cmyk.has(c)), ...spots];
  return all.join(' + ');
}

export function PantoneAutocomplete({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const { cmyk, spots } = parseParts(value);

  const q = useQuery({
    enabled: open && search.length > 0,
    queryKey: ['pantone-search', search],
    queryFn: () => api<Array<{ id: string; name: string }>>(
      `/api/lookups/pantone-colors?q=${encodeURIComponent(search)}`,
    ),
    staleTime: 30_000,
  });

  const options = q.data ?? [];
  const spotSet = new Set(spots.map(s => s.toUpperCase()));

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggleCmyk(letter: string) {
    const next = new Set(cmyk);
    if (next.has(letter)) next.delete(letter); else next.add(letter);
    onChange(buildValue(next, spots));
  }

  function addSpot(name: string) {
    if (spotSet.has(name.toUpperCase())) return;
    onChange(buildValue(cmyk, [...spots, name]));
    setSearch('');
    setOpen(false);
  }

  function removeSpot(idx: number) {
    const next = spots.filter((_, i) => i !== idx);
    onChange(buildValue(cmyk, next));
  }

  return (
    <div ref={wrapRef} className="relative">
      <label className="label">{label}</label>
      <div className="flex gap-1 mb-1">
        {CMYK.map(letter => (
          <button key={letter} type="button"
            className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${
              cmyk.has(letter)
                ? 'bg-brand text-white border-brand'
                : 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200'
            }`}
            onClick={() => toggleCmyk(letter)}>
            {letter}
          </button>
        ))}
      </div>
      {spots.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {spots.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded bg-brand-50 border border-brand-200 px-2 py-0.5 text-xs text-brand">
              {s}
              <button type="button" onClick={() => removeSpot(i)} className="text-brand/60 hover:text-brand font-bold leading-none">&times;</button>
            </span>
          ))}
        </div>
      )}
      <input
        className="input"
        value={search}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={spots.length > 0 ? 'Add another color…' : 'Type to search Pantone colors…'}
        autoComplete="off"
      />
      {open && search.length > 0 && options.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg text-sm">
          {options.slice(0, 30).map((o) => (
            <li key={o.id}
              className={`cursor-pointer px-3 py-1.5 hover:bg-brand-50 ${spotSet.has(o.name.toUpperCase()) ? 'bg-brand-50 font-medium' : ''}`}
              onMouseDown={() => addSpot(o.name)}>
              {o.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
